
import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { X, Plus, Upload } from "lucide-react";

interface ImageGalleryManagerProps {
  images: string[];
  onImagesChange: (images: string[]) => void;
}

const ImageGalleryManager = ({ images, onImagesChange }: ImageGalleryManagerProps) => {
  const [newImageUrl, setNewImageUrl] = useState("");

  const addImage = () => {
    if (!newImageUrl.trim()) return;
    onImagesChange([...images, newImageUrl.trim()]);
    setNewImageUrl("");
  };

  const removeImage = (index: number) => {
    const updated = images.filter((_, i) => i !== index);
    onImagesChange(updated);
  };

  const moveImage = (fromIndex: number, toIndex: number) => {
    const updated = [...images];
    const [movedImage] = updated.splice(fromIndex, 1);
    updated.splice(toIndex, 0, movedImage);
    onImagesChange(updated);
  };

  return (
    <Card className="admin-card border-admin-border">
      <CardHeader>
        <CardTitle className="text-admin-foreground">Product Gallery</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {images.length > 0 && (
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            {images.map((imageUrl, index) => (
              <div key={index} className="relative group">
                <img
                  src={imageUrl}
                  alt={`Product image ${index + 1}`}
                  className="w-full h-32 object-cover rounded-lg border"
                />
                <div className="absolute inset-0 bg-black bg-opacity-50 opacity-0 group-hover:opacity-100 transition-opacity rounded-lg flex items-center justify-center">
                  <Button
                    type="button"
                    variant="destructive"
                    size="sm"
                    onClick={() => removeImage(index)}
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
                <div className="absolute top-2 left-2 bg-black bg-opacity-75 text-white text-xs px-2 py-1 rounded">
                  {index + 1}
                </div>
              </div>
            ))}
          </div>
        )}

        <div className="space-y-3">
          <Label>Add Image URL</Label>
          <div className="flex gap-2">
            <Input
              value={newImageUrl}
              onChange={(e) => setNewImageUrl(e.target.value)}
              placeholder="https://example.com/image.jpg"
            />
            <Button type="button" onClick={addImage}>
              <Plus className="h-4 w-4" />
            </Button>
          </div>
        </div>

        {images.length === 0 && (
          <div className="text-center py-8 text-admin-muted-foreground">
            <Upload className="h-12 w-12 mx-auto mb-4 opacity-50" />
            <p>No images added yet</p>
            <p className="text-sm">Add image URLs to create a product gallery</p>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default ImageGalleryManager;
